import React from "react";
import {
  Col,
  Row,
  Input,
  Label,
  FormFeedback,
} from "reactstrap";
import Select from "react-select";

interface MenuFormProps {
  validation: any;
  isEdit?: boolean;
  parentMenuOptions?: Array<{ label: string; value: number }>;
  moduleOptions?: Array<{ label: string; value: number }>;
  applicationOptions?: Array<{ label: string; value: number }>;
  onApplicationChange?: (applicationId: number | null) => void;
}

const MenuForm: React.FC<MenuFormProps> = ({
  validation,
  isEdit = false,
  parentMenuOptions = [],
  moduleOptions = [],
  applicationOptions = [],
  onApplicationChange,
}) => {
  // Status options (1 for Active, 2 for Inactive)
  const statusOptions = [
    { label: "Active", value: 1 },
    { label: "Inactive", value: 2 },
  ];

  return (
    <Row className="g-3">
      <Col md={12}>
        <Label className="fw-bold mb-1">
          Application <span className="text-danger">*</span>
        </Label>
        <div style={{ width: "100%" }}>
          <Select
            options={applicationOptions}
            name="applicationid"
            placeholder="Select Application"
            value={applicationOptions.find((opt) => opt.value === Number(validation.values.applicationid)) || null}
            onChange={(e: { value: any }) => {
              const appId = e?.value || null;
              validation.setFieldValue("applicationid", appId || "");
              onApplicationChange?.(appId);
            }}
            onBlur={() => validation.setFieldTouched("applicationid", true)}
            classNamePrefix="react-select"
            className={validation.touched.applicationid && validation.errors.applicationid ? "react-select-error" : ""}
            isDisabled={isEdit}
          />
        </div>
        {validation.touched.applicationid && validation.errors.applicationid && (
          <div className="invalid-feedback d-block">{validation.errors.applicationid}</div>
        )}
      </Col>

      <Col md={12}>
        <Label className="fw-bold mb-1">
          Module <span className="text-danger">*</span>
        </Label>
        <div style={{ width: "100%" }}>
          <Select
            options={moduleOptions}
            name="moduleiid"
            placeholder="Select Module"
            value={moduleOptions.find((opt) => opt.value === Number(validation.values.moduleiid)) || null}
            onChange={(e: { value: any }) => validation.setFieldValue("moduleiid", e?.value || "")}
            onBlur={() => validation.setFieldTouched("moduleiid", true)}
            classNamePrefix="react-select"
            className={validation.touched.moduleiid && validation.errors.moduleiid ? "react-select-error" : ""}
            isDisabled={!validation.values.applicationid || isEdit}
          />
        </div>
        {validation.touched.moduleiid && validation.errors.moduleiid && (
          <div className="invalid-feedback d-block">{validation.errors.moduleiid}</div>
        )}
      </Col>

      <Col md={12}>
        <Label className="fw-bold mb-1">
          Parent Menu
        </Label>
        <div style={{ width: "100%" }}>
          <Select
            options={parentMenuOptions}
            name="parentid"
            placeholder="Select Parent Menu (Optional)"
            isClearable
            value={validation.values.parentid > 0 
              ? parentMenuOptions.find((opt) => opt.value === Number(validation.values.parentid)) || null
              : null}
            onChange={(e: { value: any } | null) => {
              const parentId = e?.value || 0;
              validation.setFieldValue("parentid", parentId);
              // Trigger routepath validation when parent changes
              if (parentId > 0 && !validation.values.routepath) {
                validation.setFieldTouched("routepath", true);
              } else if (parentId === 0) {
                // Clear routepath validation error if no parent selected
                validation.setFieldTouched("routepath", false);
              }
            }}
            onBlur={() => validation.setFieldTouched("parentid", true)}
            classNamePrefix="react-select"
            className={validation.touched.parentid && validation.errors.parentid ? "react-select-error" : ""}
          />
        </div>
        {validation.touched.parentid && validation.errors.parentid && (
          <div className="invalid-feedback d-block">{validation.errors.parentid}</div>
        )}
      </Col>

      <Col md={12}>
        <Label className="fw-bold mb-1">
          Title <span className="text-danger">*</span>
        </Label>
        <Input
          type="text"
          name="title"
          placeholder="Enter title"
          value={validation.values.title || ""}
          onChange={(e) => {
            let text = e.target.value;
            if (text.length > 70) {
              text = text.substring(0, 70);
            }
            validation.setFieldValue("title", text);
          }}
          onBlur={validation.handleBlur}
          invalid={
            !!(validation.touched.title && validation.errors.title)
          }
        />
        <FormFeedback>{validation.errors.title}</FormFeedback>
      </Col>

      <Col md={12}>
        <Label className="fw-bold mb-1">
          Description <span className="text-danger">*</span>
        </Label>
        <Input
          type="text"
          name="description"
          placeholder="Enter description"
          value={validation.values.description || ""}
          onChange={(e) => {
            let text = e.target.value;
            if (text.length > 100) {
              text = text.substring(0, 100);
            }
            validation.setFieldValue("description", text);
          }}
          onBlur={validation.handleBlur}
          invalid={
            !!(validation.touched.description && validation.errors.description)
          }
        />
        <FormFeedback>{validation.errors.description}</FormFeedback>
      </Col>

      <Col md={12}>
        <Label className="fw-bold mb-1">
          Route Path {validation.values.parentid > 0 && <span className="text-danger">*</span>}
        </Label>
        <Input
          type="text"
          name="routepath"
          placeholder="Enter route path (e.g., master/usertype)"
          value={validation.values.routepath || ""}
          onChange={(e) => {
            let text = e.target.value;
            if (text.length > 70) {
              text = text.substring(0, 70);
            }
            validation.setFieldValue("routepath", text);
          }}
          onBlur={validation.handleBlur}
          invalid={
            !!(validation.touched.routepath && validation.errors.routepath)
          }
        />
        <FormFeedback>{validation.errors.routepath}</FormFeedback>
      </Col>

      <Col md={12}>
        <Label className="fw-bold mb-1">
          Display Order <span className="text-danger">*</span>
        </Label>
        <Input
          type="number"
          name="displayorder"
          placeholder="Enter display order"
          value={validation.values.displayorder || ""}
          onChange={(e) => {
            let value = e.target.value;
            value = value.replace(/[^0-9]/g, "");
            if (value.length > 4) {
              value = value.substring(0, 4);
            }
            validation.setFieldValue("displayorder", value ? Number(value) : "");
          }}
          onBlur={validation.handleBlur}
          min={1}
          max={9999}
          invalid={
            !!(validation.touched.displayorder && validation.errors.displayorder)
          }
        />
        <FormFeedback>{validation.errors.displayorder}</FormFeedback>
      </Col>

      {isEdit && (
        <Col md={12}>
          <Label className="fw-bold mb-1">
            Status <span className="text-danger">*</span>
          </Label>
          <div style={{ width: "200px" }}>
            <Select
              options={statusOptions}
              name="status"
              placeholder="Select status"
              value={statusOptions.find((opt) => opt.value === Number(validation.values.status)) || null}
              onChange={(e: { value: any }) => validation.setFieldValue("status", e?.value)}
              onBlur={() => validation.setFieldTouched("status", true)}
              classNamePrefix="react-select"
              className={validation.touched.status && validation.errors.status ? "react-select-error" : ""}
            />
          </div>
          {validation.touched.status && validation.errors.status && (
            <div className="invalid-feedback d-block">{validation.errors.status}</div>
          )}
        </Col>
      )}
    </Row>
  );
};

export default MenuForm;